import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Template tabs state
  openTemplateTabs: [],       // Array of tab objects: { id, template_name, fabric_id, fabric_name, vm_config, lang_id }
  activeTabId: null,           // Currently active tab ID
  templateDataCache: {},       // Maps templateId -> fetched template data

  // Per-tab UI state
  tabActiveViews: {},          // Maps tabId -> 'template' | 'valuemessage' | 'report'
  tabVmConfig: {},             // Maps tabId -> boolean (vm_config flag)
  tabExpandedSections: {},     // Maps tabId -> { sectionKey: bool } for template viewer
  tabRefAgentExpanded: {},     // Maps tabId -> { sectionKey: bool } for ReferenceAgentSelector
  tabWritingRulesPending: {},  // Maps tabId -> { agentId: { version (latest when applied), requestId } }
  tabWritingRulesNoChange: {}, // Maps tabId -> { agentId: version the writing rules left unchanged }

  // Form state
  isFormVisible: false,
  editingTemplate: null,       // Template object being edited, or null

  // Navigation state
  selectedFabricId: null,      // Fabric ID from Project card click — persists across navigation
  fabricTabsCache: {},
};

const genbiSlice = createSlice({
  name: 'genbi',
  initialState,
  reducers: {

    // ── Template Tabs ──────────────────────────────────────
    addTemplateTab(state, action) {
      if (!state.openTemplateTabs.some(tab => tab.id === action.payload.id)) {
        state.openTemplateTabs.push(action.payload);
      }
    },

    removeTemplateTab(state, action) {
      const templateId = action.payload;
      state.openTemplateTabs = state.openTemplateTabs.filter(
        (tab) => tab.id !== templateId
      );
      delete state.templateDataCache[templateId];
      delete state.tabActiveViews[templateId];
      delete state.tabVmConfig[templateId];
      delete state.tabExpandedSections[templateId];
      delete state.tabRefAgentExpanded[templateId];
      delete state.tabWritingRulesPending[templateId];
      delete state.tabWritingRulesNoChange[templateId];

      // If closing the active tab, switch to last remaining tab
      if (state.activeTabId === templateId) {
        const remaining = state.openTemplateTabs;
        state.activeTabId =
          remaining.length > 0 ? remaining[remaining.length - 1].id : null;
      }
    },

    updateTemplateTabName(state, action) {
      const { tabId, template_name } = action.payload;
      const tab = state.openTemplateTabs.find(t => t.id === tabId);
      if (tab) {
        tab.template_name = template_name;
      }
    },

    setActiveTabId(state, action) {
      state.activeTabId = action.payload;
    },

    // ── Template Data Cache ────────────────────────────────
    updateTemplateDataCache(state, action) {
      const { templateId, data } = action.payload;
      state.templateDataCache[templateId] = data;
    },

    setTemplateDataCacheFull(state, action) {
      state.templateDataCache = action.payload;
    },

    // Drop one project's cached template data (open and parked tabs) so the
    // next tab mount refetches
    invalidateFabricTemplateCache(state, action) {
      const fabricId = Number(action.payload);
      if (state.fabricTabsCache[fabricId]) {
        state.fabricTabsCache[fabricId].templateDataCache = {};
      }
      state.openTemplateTabs.forEach(tab => {
        if (Number(tab.fabric_id) === fabricId) {
          delete state.templateDataCache[tab.id];
        }
      });
    },

    // ── Per-Tab Views ──────────────────────────────────────
    setTabActiveView(state, action) {
      const { tabId, view } = action.payload;
      state.tabActiveViews[tabId] = view;
    },

    setTabVmConfigValue(state, action) {
      const { tabId, config } = action.payload;
      state.tabVmConfig[tabId] = config;
    },

    setTabExpandedSections(state, action) {
      const { tabId, expandedSections } = action.payload;
      state.tabExpandedSections[tabId] = expandedSections;
    },

    setTabRefAgentExpanded(state, action) {
      const { tabId, expandedSections } = action.payload;
      state.tabRefAgentExpanded[tabId] = expandedSections;
    },

    // ── Writing rules "no changes" pill (session only) ─────
    // Not persisted: a page reload or closing the tab clears it.
    setWritingRulesPending(state, action) {
      const { tabId, versions } = action.payload; // { agentId: { version, requestId } }
      const pending = { ...(state.tabWritingRulesPending[tabId] || {}), ...versions };
      const noChange = { ...(state.tabWritingRulesNoChange[tabId] || {}) };
      Object.keys(versions).forEach(id => { delete noChange[id]; });
      state.tabWritingRulesPending[tabId] = pending;
      state.tabWritingRulesNoChange[tabId] = noChange;
    },

    resolveWritingRules(state, action) {
      const { tabId, done, noChange } = action.payload; // done: [agentId], noChange: { agentId: version }
      const pending = { ...(state.tabWritingRulesPending[tabId] || {}) };
      done.forEach(id => { delete pending[id]; });
      state.tabWritingRulesPending[tabId] = pending;
      state.tabWritingRulesNoChange[tabId] = {
        ...(state.tabWritingRulesNoChange[tabId] || {}),
        ...noChange,
      };
    },

    // Hide the "No changes made" pills of a tab (Refresh, reopen from the sidebar).
    // Agents still pending are kept and checked on the next fetch.
    clearWritingRulesNoChange(state, action) {
      delete state.tabWritingRulesNoChange[action.payload]; // tabId
    },

    // ── Form State ─────────────────────────────────────────
    setIsFormVisible(state, action) {
      state.isFormVisible = action.payload;
    },

    setEditingTemplate(state, action) {
      state.editingTemplate = action.payload;
    },

    // ── Navigation State ───────────────────────────────────
    setSelectedFabricId(state, action) {
      state.selectedFabricId = action.payload;
    },

    switchFabric(state, action) {
      const newFabricId = action.payload;
      const oldFabricId = state.selectedFabricId;

      // eslint-disable-next-line eqeqeq
      if (oldFabricId == newFabricId) return;

      if (oldFabricId && state.openTemplateTabs.length > 0) {
        state.fabricTabsCache[oldFabricId] = {
          openTemplateTabs: state.openTemplateTabs,
          activeTabId: state.activeTabId,
          templateDataCache: state.templateDataCache,
          tabActiveViews: state.tabActiveViews,
          tabVmConfig: state.tabVmConfig,
          tabExpandedSections: state.tabExpandedSections,
          tabRefAgentExpanded: state.tabRefAgentExpanded,
          tabWritingRulesPending: state.tabWritingRulesPending,
          tabWritingRulesNoChange: state.tabWritingRulesNoChange,
        };
      }

      const cached = state.fabricTabsCache[newFabricId];
      if (cached) {
        state.openTemplateTabs = cached.openTemplateTabs;
        state.activeTabId = cached.activeTabId;
        state.templateDataCache = cached.templateDataCache;
        state.tabActiveViews = cached.tabActiveViews;
        state.tabVmConfig = cached.tabVmConfig;
        state.tabExpandedSections = cached.tabExpandedSections || {};
        state.tabRefAgentExpanded = cached.tabRefAgentExpanded || {};
        state.tabWritingRulesPending = cached.tabWritingRulesPending || {};
        state.tabWritingRulesNoChange = cached.tabWritingRulesNoChange || {};
        delete state.fabricTabsCache[newFabricId];
      } else {
        state.openTemplateTabs = [];
        state.activeTabId = null;
        state.templateDataCache = {};
        state.tabActiveViews = {};
        state.tabVmConfig = {};
        state.tabExpandedSections = {};
        state.tabRefAgentExpanded = {};
        state.tabWritingRulesPending = {};
        state.tabWritingRulesNoChange = {};
      }

      state.selectedFabricId = newFabricId;

      
      state.isFormVisible = false;
      state.editingTemplate = null;
    },

    // ── Clear all tabs (when switching to different fabric) ──
    clearAllTabs(state) {
      state.openTemplateTabs = [];
      state.activeTabId = null;
      state.templateDataCache = {};
      state.tabActiveViews = {};
      state.tabVmConfig = {};
      state.tabExpandedSections = {};
      state.tabRefAgentExpanded = {};
      state.tabWritingRulesPending = {};
      state.tabWritingRulesNoChange = {};
      state.selectedFabricId = null;

      // Same guard as switchFabric — category change also counts as a project switch
      state.isFormVisible = false;
      state.editingTemplate = null;
    },

    // ── Close one project's tabs (access to it is gone) ────
    // Its open tabs if it is the current project, and any it has parked in
    // fabricTabsCache — so they can't come back on a later switch. Other
    // projects' parked tabs are left alone.
    closeFabricTabs(state, action) {
      const fabricId = Number(action.payload);
      delete state.fabricTabsCache[fabricId];
      const isCurrent = Number(state.selectedFabricId) === fabricId
        || state.openTemplateTabs.some(tab => Number(tab.fabric_id) === fabricId);
      if (!isCurrent) return;
      state.openTemplateTabs = [];
      state.activeTabId = null;
      state.templateDataCache = {};
      state.tabActiveViews = {};
      state.tabVmConfig = {};
      state.tabExpandedSections = {};
      state.tabRefAgentExpanded = {};
      state.tabWritingRulesPending = {};
      state.tabWritingRulesNoChange = {};
      state.selectedFabricId = null;
      state.isFormVisible = false;
      state.editingTemplate = null;
    },

    // ── Bulk / Reset ───────────────────────────────────────
    resetGenbiState() {
      return initialState;
    },
  },
});

export const {
  addTemplateTab,
  removeTemplateTab,
  updateTemplateTabName,
  setActiveTabId,
  updateTemplateDataCache,
  setTemplateDataCacheFull,
  invalidateFabricTemplateCache,
  setTabActiveView,
  setTabVmConfigValue,
  setTabExpandedSections,
  setTabRefAgentExpanded,
  setWritingRulesPending,
  resolveWritingRules,
  clearWritingRulesNoChange,
  setIsFormVisible,
  setEditingTemplate,
  setSelectedFabricId,
  switchFabric,
  clearAllTabs,
  closeFabricTabs,
  resetGenbiState,
} = genbiSlice.actions;

export default genbiSlice.reducer;
